import ArticleHeroEyebrow from 'components/ArticleHeroEyebrow';
import FeatureByline from 'components/FeatureByline';
import FeaturedHeroMedia from 'components/FeaturedHeroMedia';
import GeolocationLabel from 'components/GeolocationLabel';
import getArticlePageTestId, { ARTICLE_PAGE_TEST_IDS } from 'constants/testsIds/articlePage';
import type { HeroConfig } from 'interfaces/content/articles/Articles';
import type React from 'react';
import { capitalize } from 'lodash';
import * as S from './heroStyles';
import { useOptimizely } from 'providers/OptimizelyProvider';

const Hero: React.FC<HeroConfig> = ({ article }) => {
  const { featuredMediaType, imageCaption, imageCredit, postSettings, videoId, title, relatedVideoType, sections } =
    article;

  const { userFlags: userVariants } = useOptimizely();
  const variants = userVariants?.asiaMigration as { variant: string };
  const variantToUse = variants?.variant;
  const asiaMigrationEnabled = variantToUse !== 'off';

  const primarySection = {
    link: article.primarySection.uri,
    name: article.primarySection.name,
  };

  const primaryPostTag = {
    link: article.primaryPostTag.uri,
    name: !article.primaryPostTag?.disablePage ? article.primaryPostTag.name : '',
  };

  const trackingData = {
    contentPlacementCD: 'article side by side hero',
    eventAction: 'hero click',
    eventCategory: 'navigation',
  };

  const hasImageOrVideo = featuredMediaType || videoId;
  // Display only one video; If we have a related video, fall featured media back to an image
  const overrideFeaturedMediaType = relatedVideoType === 'stn_video_media' ? 'image' : featuredMediaType;

  return (
    <S.HeroAndImageWrapper data-cy={ARTICLE_PAGE_TEST_IDS.WRAPPER}>
      <S.RedesignedHeroWrapper data-cy={getArticlePageTestId('EYEBROW')}>
        <S.EyebrowGeoLabelWrapper>
          <ArticleHeroEyebrow
            primarySection={primarySection}
            primaryPostTag={primaryPostTag}
            className='eyebrow'
            trackingData={trackingData}
          />
          <S.GeoLabelWrapper>
            {asiaMigrationEnabled
              ? sections.map((section) => {
                  if (['asia', 'europe'].includes(section.slug)) {
                    return (
                      <GeolocationLabel
                        key={section.slug}
                        title={capitalize(section.slug)}
                        href={`/section/${section.slug}`}
                      />
                    );
                  }
                  return null;
                })
              : sections.map((section) => {
                  if (section.slug === 'asia') {
                    return (
                      <GeolocationLabel
                        key={section.slug}
                        title={capitalize(section.slug)}
                        href={`/${section.slug}`}
                      />
                    );
                  }
                  if (section.slug === 'europe') {
                    return (
                      <GeolocationLabel
                        key={section.slug}
                        title={capitalize(section.slug)}
                        href={`/section/${section.slug}`}
                      />
                    );
                  }
                  return null;
                })}
          </S.GeoLabelWrapper>
        </S.EyebrowGeoLabelWrapper>
        <S.RedesignedHeroTitle
          data-cy={getArticlePageTestId('TITLE')}
          as='h1'
          $fontFamily='saol'
          $size={{ default: 'level3', mdMin: 'large' }}
        >
          {title}
        </S.RedesignedHeroTitle>
        <FeatureByline article={article} />
      </S.RedesignedHeroWrapper>

      {hasImageOrVideo && (
        <S.RedesignedHeroImageContainer data-cy={getArticlePageTestId('IMAGE')}>
          <FeaturedHeroMedia
            className='article-video'
            featuredMediaType={overrideFeaturedMediaType}
            imageCaption={imageCaption}
            imageCredit={imageCredit}
            postSettings={postSettings}
            videoId={videoId}
          />
        </S.RedesignedHeroImageContainer>
      )}
    </S.HeroAndImageWrapper>
  );
};

export default Hero;
